export const prerender = true;
export const trailingSlash = 'never';

import { redirect } from '@sveltejs/kit';

import { browser } from '$app/environment';
import posthog from 'posthog-js';
import { env } from '$env/dynamic/public';

import { createAPIClients } from '$lib/api';
import { prefetchIsLegalAccepted } from '$lib/api/end-user';
import { isUnauthenticatedError } from '$lib/api/errors';
import {
  initializeDefaultOrgID,
  prefetchListOrganizations,
} from '$lib/api/orgs';
import { prefetchAppUser } from '$lib/api/user';
import { createAuthContext } from '$lib/auth';

import type { LayoutLoad } from './$types';

export const load: LayoutLoad = async ({ url }) => {
  if (browser) {
    const key =
      window.location.hostname === 'app.viam.com'
        ? env.PUBLIC_POSTHOG_KEY_PROD
        : env.PUBLIC_POSTHOG_KEY_STAGING;
    const host = env.PUBLIC_POSTHOG_HOST;

    if (key && host) {
      posthog.init(key, {
        api_host: host,
        capture_pageview: false,
        capture_pageleave: false,
        capture_exceptions: false,
      });
    }
  }

  const authContext = createAuthContext();
  const { queryClient, serviceClients } = createAPIClients(authContext);
  let initialOrgID: string | undefined;

  if (browser) {
    await authContext.initialize();
    const { data: appUserData } = await prefetchAppUser(
      queryClient,
      serviceClients.internalUser,
      authContext
    );

    const [{ error: isLegalAcceptedError }, { data: orgs }] = await Promise.all(
      [
        prefetchIsLegalAccepted(
          queryClient,
          serviceClients.endUser,
          authContext
        ),
        prefetchListOrganizations(queryClient, serviceClients.app, authContext),
      ]
    );

    if (isUnauthenticatedError(isLegalAcceptedError)) {
      authContext.logout();
    }

    const result = initializeDefaultOrgID(
      orgs,
      appUserData?.user?.orgLastAccess
    );
    initialOrgID = result.orgID;

    if (result.orgNotAccessible && url.pathname !== '/org-not-accessible') {
      redirect(302, '/org-not-accessible');
    }
  }

  return {
    authContext,
    queryClient,
    serviceClients,
    initialOrgID,
  };
};
